import React, { useEffect } from 'react';
import {Form, Input, Radio, DatePicker, Select, Checkbox, Row, Col} from 'antd';
import ConditionalFormItem from '../../ConditionalFormItem';
import {SERVICE_TYPE_MAP} from "../../../config/service-type-map";
import NATION_OPTIONS from "../../../config/nations";
import feeService from '../../../services/fee';
const { Option } = Select;

interface CopyrightBusinessTabProps {
  serviceType: string;
  getReadOnlyProps?: (fieldType?: 'input' | 'select') => any;
  baseCountryMode?: boolean;
}

// 版权业务信息 Tab：从 DynamicOrderForm 中的内联 Business 提取
const CopyrightSection: React.FC<CopyrightBusinessTabProps> = ({ serviceType, getReadOnlyProps = () => ({}), baseCountryMode = false }) => {
  const serviceTypeOptions = SERVICE_TYPE_MAP[serviceType] || {};
  const formInstance = Form.useFormInstance();
  const watchedNation = Form.useWatch('nation', formInstance);

  useEffect(() => {
    const loadChannels = async () => {
      try {
        if (!watchedNation) {
          return;
        }
        await feeService.getSubmitChannelsByNation(watchedNation as string);
      } catch (e) {
        // ignore
      }
    };
    loadChannels();
  }, [watchedNation]);
  return (
    <>
      {/* 子业务名称 */}
      <Form.Item label="子业务名称" name="sub_service" rules={[{ required: true, message: '请选择子业务名称' }]}>
        <Select placeholder="请选择具体的子业务名称">
          {(Object.entries(serviceTypeOptions) as [string, string][]).map(([key, value]) => (
            <Option key={key} value={key}>
              {value}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label={baseCountryMode ? '基础国家/地区' : '国家/地区'}
        name="nation"
        rules={[{ required: true, message: baseCountryMode ? '请选择基础国家/地区' : '请选择国家/地区' }]}
      >
        <Select
          placeholder={baseCountryMode ? '请选择基础国家/地区' : '请选择国家/地区'}
          showSearch
          filterOption={(input, option) =>
            String(option?.children || '').toLowerCase().includes(input.toLowerCase())
          }
          data-testid="order-nation"
        >
          {NATION_OPTIONS.map(option => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="订单编号" name="order_no" rules={[{ required: true, message: '订单编号自动生成' }]}>
            <Input placeholder="自动生成" readOnly style={{ backgroundColor: '#f5f5f5', color: '#999' }} data-testid="order-no" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="案件编号" name="case_no">
            <Input placeholder="客户内部/对外案件编号（可选）" data-testid="order-case-no" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label="作品名称" name="work_name" rules={[{ required: true, message: '请输入作品名称' }]}>
        <Input placeholder="请输入作品名称" />
      </Form.Item>

      <ConditionalFormItem condition="sub_service != transfer">
        <Form.Item label="作品状态" required>
          <Form.Item name="open_status" noStyle initialValue={'未公开'}>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {() => {
              const checked = formInstance.getFieldValue('open_status') === '已公开';
              return (
                <Checkbox
                  checked={checked}
                  onChange={(e) => formInstance.setFieldsValue({ open_status: e.target.checked ? '已公开' : '未公开' })}
                >
                  已公开
                </Checkbox>
              );
            }}
          </Form.Item>
        </Form.Item>
      </ConditionalFormItem>

      <ConditionalFormItem condition="open_status == 已公开">
        <Form.Item label="公开国家和城市" name="opened_area">
          <Input placeholder="请输入公开国家和城市" />
        </Form.Item>
        <Form.Item label="公开日期" name="opened_at">
          <DatePicker style={{ width: '100%' }} placeholder="请选择公开日期" />
        </Form.Item>
        <Form.Item label="公开方式" name="opened_type">
          <Radio.Group>
            <Radio value="线上">线上</Radio>
            <Radio value="线下">线下</Radio>
            <Radio value="其他">其他</Radio>
          </Radio.Group>
        </Form.Item>
      </ConditionalFormItem>

      <ConditionalFormItem condition="sub_service != transfer">
        <Form.Item label="申请方式" name="application_type">
          <Radio.Group>
            <Radio value="单一作品">单一作品</Radio>
            <Radio value="系列作品">系列作品</Radio>
          </Radio.Group>
        </Form.Item>
      </ConditionalFormItem>

      <Form.Item label="作品类型" name="application_category">
        <Radio.Group>
          <Radio value="二维美术作品">二维美术作品</Radio>
          <Radio value="3D工业设计作品">3D工业设计作品</Radio>
          <Radio value="影音作品">影音作品</Radio>
          <Radio value="摄影作品">摄影作品</Radio>
          <Radio value="计算机软件作品">计算机软件作品</Radio>
        </Radio.Group>
      </Form.Item>

      <ConditionalFormItem condition="sub_service != transfer">
        <Form.Item label="作品来源及灵感" name="work_inspiration">
          <Input.TextArea rows={3} placeholder="请输入作品来源及灵感" />
        </Form.Item>
      </ConditionalFormItem>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="申请号" name="application_no">
            <Input placeholder="请输入申请号" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="注册号" name="registration_no">
            <Input placeholder="请输入注册号" />
          </Form.Item>
        </Col>
      </Row>

      <ConditionalFormItem condition="sub_service == transfer">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="受让人名字" name="assignee_name">
              <Input placeholder="请输入受让人名字" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="受让人英文名字" name="assignee_name_en">
              <Input placeholder="请输入受让人英文名字" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="受让人地址" name="assignee_address">
              <Input placeholder="请输入受让人地址" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="受让人地址英文" name="assignee_address_en">
              <Input placeholder="请输入受让人地址英文" />
            </Form.Item>
          </Col>
        </Row>
      </ConditionalFormItem>

      <Form.Item label="备注" name="remark">
        <Input.TextArea rows={3} placeholder="请输入备注信息" data-testid="order-remark" />
      </Form.Item>
    </>
  );
};

export default CopyrightSection;
